import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Section, Container, SectionTitle, SectionSubtitle } from '../../styles/StyledComponents';

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-top: 3rem;
  
  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled(motion.div)`
  background-color: ${({ theme }) => theme.colors.white};
  border-radius: 8px;
  padding: 2.5rem;
  box-shadow: ${({ theme }) => theme.shadows.medium};
  transition: transform 0.3s ease, box-shadow 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: ${({ theme }) => theme.shadows.large};
  }
`;

const BenefitIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.primary};
  
  .material-icons {
    font-size: 2.5rem;
  }
`;

const BenefitTitle = styled.h3`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  color: ${({ theme }) => theme.colors.secondary};
`;

const BenefitDescription = styled.div`
  font-size: 1rem;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text};
  
  p {
    margin-bottom: 1rem;
  }
  
  p:last-child {
    margin-bottom: 0;
  }
`;

const BenefitSubtitle = styled.p`
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const BenefitList = styled.ul`
  list-style-type: none;
  padding-left: 0;
  margin-bottom: 1rem;
  
  li {
    margin-bottom: 0.5rem;
    padding-left: 1.5rem;
    position: relative;
    
    &:before {
      content: "•";
      position: absolute;
      left: 0;
      color: ${({ theme }) => theme.colors.primary};
    }
  }
`;

const SectionTitleWithIcon = styled(SectionTitle)`
  display: flex;
  align-items: center;
  
  .material-icons {
    margin-right: 0.5rem;
    font-size: 2rem;
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const benefits = [
  {
    id: 1,
    icon: 'search',
    title: 'Clarity on What to Automate',
    description: (
      <>
        <p>Most teams waste time building the wrong things. What businesses actually need usually boils down to:</p>
        <BenefitList>
          <li>Not enough sales</li>
          <li>Can't deliver fast enough</li>
          <li>Wasting time on back-office support (Accounting, Legal, HR, etc.)</li>
        </BenefitList>
        <p>We help you figure out where AI can actually move the needle before you sink months into building.</p>
      </>
    )
  },
  {
    id: 2,
    icon: 'psychology',
    title: 'Don\'t Be Evil: 2025 Edition',
    description: (
      <>
        <p>Google dropped the motto. We're picking it back up.</p>
        <p>AI agents shouldn't replace jobs. They should make them less stressful, more impactful, and honestly, more fun.</p>
        <p>We're not building automation to fire your team. We're building it to free them up to do what they're great at.</p>
      </>
    )
  },
  {
    id: 3,
    icon: 'build',
    title: 'Systems That Actually Ship',
    description: (
      <>
        <p>We don't do innovation theater.</p>
        <p>We help you scope it, build it, connect it to your existing systems and get it live without adding six layers of procurement purgatory.</p>
      </>
    )
  },
  {
    id: 4,
    icon: 'cloud',
    title: 'Cloud-Native, Scale-Ready',
    description: (
      <>
        <p>Our superpower? Cloud architecture.</p>
        <p>We have the expertise to design systems that aren't just smart. They're built to scale.</p>
        <p>Elastic, secure, observable, and ready to grow as fast as you do.</p>
      </>
    )
  },
  {
    id: 5,
    icon: 'link',
    title: 'Integrated with Your Stack',
    description: (
      <>
        <p>We meet you where you are. Our systems plug into the tools your team already uses, so adoption is fast and disruption is minimal.</p>
      </>
    )
  },
  {
    id: 6,
    icon: 'lock',
    title: 'Security That Doesn\'t Stall You',
    description: (
      <>
        <p>Enterprise security should be a starting point, not a final invoice.</p>
        <p>We bake in smart defaults: least-privilege access, clear audit trails, data boundaries, and compliance-ready practices.</p>
        <p>Not every company starts there, but we'll get you on that path faster than most.</p>
      </>
    )
  }
];

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: {
      delay: i * 0.1,
      duration: 0.5
    }
  })
};

const Benefits = () => {
  return (
    <Section id="benefits">
      <Container>
        <SectionTitleWithIcon
          as={motion.h2}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <span className="material-icons">auto_awesome</span>
          What You Get When You Work With Us
        </SectionTitleWithIcon>
        
        <BenefitsGrid>
          {benefits.map((benefit, index) => (
            <BenefitCard
              key={benefit.id}
              as={motion.div}
              custom={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <BenefitIcon>
                <span className="material-icons">{benefit.icon}</span>
              </BenefitIcon>
              <BenefitTitle>{benefit.title}</BenefitTitle>
              <BenefitDescription>{benefit.description}</BenefitDescription>
            </BenefitCard>
          ))}
        </BenefitsGrid>
      </Container>
    </Section>
  );
};

export default Benefits;
